import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, CornerDownLeft } from 'lucide-react'
import { useGetGlobalSearch } from '../../hooks/backend/search'
import { Chip } from '../interviews/Chip'
import { cn } from '../../lib/shadcn/utils'
import { tidyResults, kindLabel, kindTone, moveActive, isSearchable, type SearchResult } from '../../utils/search'

// The search box in the top bar. Searches names, emails, phone numbers, ID
// numbers and plates across drivers, applicants, rejected applicants, Academy
// members, owners, vehicles and the team, and says under each suggestion what
// it is and which tab it is on. Enter or a click opens it there.
export function GlobalSearch({ className }: { className?: string }) {
  const navigate = useNavigate()
  const search = useGetGlobalSearch()
  const trigger = search.trigger
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [results, setResults] = useState<SearchResult[]>([])
  const [searched, setSearched] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const seq = useRef(0)

  // Ask once the typing pauses. A reply to an older query is thrown away, so
  // a slow answer can never overwrite a newer one.
  useEffect(() => {
    if (!isSearchable(q)) {
      setResults([])
      setSearched('')
      return
    }
    const mine = ++seq.current
    const t = setTimeout(async () => {
      try {
        const data = await trigger({ q: q.trim(), limit: 30 }, { skipCache: true })
        if (mine !== seq.current) return
        setResults(tidyResults(data))
        setSearched(q.trim())
        setActive(-1)
      } catch {
        if (mine !== seq.current) return
        setResults([])
        setSearched(q.trim())
      }
    }, 250)
    return () => clearTimeout(t)
  }, [q, trigger])

  // Close on a click anywhere else.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  // Ctrl K or Cmd K puts the cursor in the box from anywhere.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  function go(r: SearchResult) {
    if (!r.route) return
    setOpen(false)
    setQ('')
    inputRef.current?.blur()
    navigate(r.route)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => moveActive(a, 1, results.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => moveActive(a, -1, results.length))
    } else if (e.key === 'Enter') {
      const r = results[active >= 0 ? active : 0]
      if (r) go(r)
    } else if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  const showPanel = open && isSearchable(q)
  const waiting = search.loading || searched !== q.trim()

  return (
    <div ref={boxRef} className={cn('relative w-full max-w-xl', className)}>
      <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search drivers, applicants, owners, plates, team..."
          aria-label="Search the cockpit"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="global-search-results"
          className="h-9 w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        {q && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQ('')
              inputRef.current?.focus()
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showPanel && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-card shadow-lg"
        >
          {results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              {waiting ? 'Searching...' : `Nothing found for "${q.trim()}".`}
            </p>
          ) : (
            <ul className="py-1">
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.ref_id ?? i}-${r.route ?? ''}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === active}
                    disabled={!r.route}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                    className={cn(
                      'flex w-full items-start gap-3 px-3 py-2 text-left',
                      i === active ? 'bg-muted' : '',
                      r.route ? 'cursor-pointer' : 'cursor-default',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium text-foreground">{r.title}</span>
                        <Chip tone={kindTone(r.kind)}>{kindLabel(r)}</Chip>
                      </div>
                      {r.subtitle && <p className="truncate text-xs text-muted-foreground">{r.subtitle}</p>}
                      <p className="text-[11px] text-muted-foreground">
                        {r.route ? `Found in ${r.where_label ?? 'the cockpit'}` : `${r.where_label ?? 'Team'}: no page to open`}
                      </p>
                    </div>
                    {i === active && r.route && <CornerDownLeft className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
